import { logger } from '@gik/analytics/utils/logger';
import type { SimilarProductsParams } from '@gik/api/products/similarProducts';
import { useSimilarProducts } from '@gik/api/products/similarProducts';
import { FetchProvider } from '@gik/core/components/DynamicComponent/FetchProvider';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import {
  RelatedProducts,
  RelatedProductsSkeleton,
} from '@gik/shop/components/products/RelatedProducts/RelatedProducts';
import { Container } from '@gik/ui/Container';
import type { PageSectionProps } from '@gik/ui/gik/PageSection';
import { PageSection } from '@gik/ui/gik/PageSection';
import type { HeadingLevel } from '@gik/ui/Heading';
import { Heading } from '@gik/ui/Heading';
import React from 'react';

export interface IRelatedProductsSectionProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Pick<PageSectionProps, 'variant'> {
  copy?: string;
  searchParams: SimilarProductsParams;
  headingLevel?: HeadingLevel;
}

export default function RelatedProductsSectionComp({
  className,
  copy = 'You might also like',
  searchParams,
  variant = 'lightblue',
  headingLevel,
}: IRelatedProductsSectionProps) {
  const bem = bemBlock('related-products-section');

  const useFetch = () => {
    return useSimilarProducts({ ...searchParams });
  };
  return (
    <FetchProvider
      loadingComponent={
        <PageSection variant={variant} className={bem(null, null, className)}>
          <RelatedProductsSkeleton skeletonItems={4} />{' '}
        </PageSection>
      }
      fetch={useFetch}
      transform={data => {
        if (Array.isArray(data)) return data;

        logger.error(data);
        return [];
      }}
    >
      {({ transformed }) =>
        transformed?.length > 0 && (
          <PageSection variant={variant} className={bem(null, null, className)}>
            <Container>
              <Heading level={headingLevel} className={bem('title')}>
                {copy}
              </Heading>
              <RelatedProducts searchParams={searchParams} products={transformed} />
            </Container>
          </PageSection>
        )
      }
    </FetchProvider>
  );
}

export const RelatedProductsSection = withComponentErrorBoundary(RelatedProductsSectionComp);
